import { Component, computed, DestroyRef, inject, signal, type OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CanDeactivateFn } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { FileExplorerEntry, FileExplorerReadResponse, FileExplorerFileInfo } from 'src/api/api.service';
import { FILE_EXPLORER_SERVICE } from 'src/api/file-explorer.service.interface';
import { PageHeaderComponent } from '@/shared/page-header/page-header.component';
import { TranslationService } from '@/layout/service/translation.service';
import hljs from 'highlight.js';

export const fileExplorerCanDeactivate: CanDeactivateFn<{ canDeactivate(): Promise<boolean> }> = (component) => component.canDeactivate();

@Component({
    selector: 'app-file-explorer',
    imports: [FormsModule, ButtonModule, InputTextModule, TooltipModule, ConfirmDialogModule, ToastModule, PageHeaderComponent],
    templateUrl: './file-explorer.component.html',
    styleUrl: './file-explorer.component.scss',
    providers: [ConfirmationService, MessageService],
    host: { '(window:beforeunload)': 'onBeforeUnload($event)' }
})
export class FileExplorerComponent implements OnInit {
    private readonly api = inject(FILE_EXPLORER_SERVICE);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly messageService = inject(MessageService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly translation = inject(TranslationService);
    readonly t = this.translation.t;
    private directoryRequest = 0;
    private previewRequest = 0;
    private previewObjectUrl: string | null = null;

    readonly entries = signal<FileExplorerEntry[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);
    readonly rootDirectory = signal('');
    readonly currentPath = signal('');
    readonly search = signal('');
    readonly sort = signal('name');
    readonly selectedFile = signal<FileExplorerEntry | null>(null);
    readonly previewLoading = signal(false);
    readonly previewError = signal<string | null>(null);
    readonly previewContent = signal<FileExplorerReadResponse | null>(null);
    readonly fileInfo = signal<FileExplorerFileInfo | null>(null);
    readonly imageUrl = signal<string | null>(null);
    readonly pdfUrl = signal<SafeResourceUrl | null>(null);
    readonly previewExpanded = signal(false);
    readonly editMode = signal(false);
    readonly editContent = signal('');
    readonly editSaving = signal(false);
    readonly uploading = signal(false);
    readonly uploadStatus = signal('');
    readonly uploadError = signal<string | null>(null);
    readonly deleting = signal(false);
    readonly downloading = signal(false);
    readonly isDragOver = signal(false);
    readonly busy = computed(() => this.editSaving() || this.uploading() || this.deleting());
    readonly dirty = computed(() => this.editMode() && this.editContent() !== (this.previewContent()?.content ?? ''));
    readonly canEdit = computed(
        () => this.fileInfo()?.previewType === 'text' && this.previewContent()?.content != null && !this.previewContent()?.isTruncated && !this.previewContent()?.isBinary && !this.previewContent()?.warning && !this.fileInfo()?.binaryWarning
    );
    readonly folderCount = computed(() => this.entries().filter((entry) => entry.type === 'directory').length);
    readonly fileCount = computed(() => this.entries().length - this.folderCount());
    readonly visibleEntries = computed(() => {
        const query = this.search().trim().toLocaleLowerCase();
        const sort = this.sort();
        return this.entries()
            .filter((entry) => entry.name.toLocaleLowerCase().includes(query))
            .sort((a, b) => {
                if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
                if (sort === 'size') {
                    const difference = (b.sizeBytes ?? -1) - (a.sizeBytes ?? -1);
                    if (difference) return difference;
                }
                if (sort === 'modified') {
                    const difference = (Date.parse(b.modifiedUtc) || 0) - (Date.parse(a.modifiedUtc) || 0);
                    if (difference) return difference;
                }
                return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
            });
    });
    readonly breadcrumbs = computed(() => {
        const items = [{ label: this.t()('files.root'), path: '' }];
        let path = '';
        for (const part of this.currentPath().split('/').filter(Boolean)) {
            path += `/${part}`;
            items.push({ label: part, path });
        }
        return items;
    });
    readonly highlightedCode = computed(() => {
        const content = this.previewContent()?.content ?? '';
        const language = this.fileInfo()?.language || 'plaintext';
        return hljs.highlight(content, { language: hljs.getLanguage(language) ? language : 'plaintext' }).value;
    });
    readonly lineNumbers = computed(() => Array.from({ length: (this.previewContent()?.content ?? '').split('\n').length }, (_, index) => index + 1));

    constructor() {
        inject(DestroyRef).onDestroy(() => {
            this.directoryRequest++;
            this.previewRequest++;
            this.revokePreviewUrl();
        });
    }

    ngOnInit(): void {
        void this.loadDirectory('');
    }

    async navigateTo(path: string): Promise<void> {
        if (!(await this.canDeactivate())) return;
        this.search.set('');
        await this.loadDirectory(path);
    }

    async refresh(): Promise<void> {
        if (await this.canDeactivate()) await this.loadDirectory(this.currentPath());
    }

    goUp(): Promise<void> {
        const parts = this.currentPath().split('/').filter(Boolean);
        parts.pop();
        return this.navigateTo(parts.length ? `/${parts.join('/')}` : '');
    }

    private async loadDirectory(path: string): Promise<void> {
        const request = ++this.directoryRequest;
        this.resetPreview();
        this.currentPath.set(path === '/' ? '' : path);
        this.entries.set([]);
        this.error.set(null);
        this.uploadError.set(null);
        this.loading.set(true);
        try {
            const result = await this.api.fileExplorerListDir(path);
            if (request !== this.directoryRequest) return;
            if (!result.success || !result.entries) throw new Error(result.error || this.t()('files.loadError'));
            this.entries.set(result.entries);
            if (result.rootDirectory != null) this.rootDirectory.set(result.rootDirectory);
        } catch (error) {
            if (request === this.directoryRequest) this.error.set(this.errorMessage(error));
        } finally {
            if (request === this.directoryRequest) this.loading.set(false);
        }
    }

    async openEntry(entry: FileExplorerEntry): Promise<void> {
        if (this.selectedFile()?.path === entry.path || !(await this.canDeactivate())) return;
        if (entry.type === 'directory' && !entry.isSymlink) {
            this.search.set('');
            await this.loadDirectory(entry.path);
            return;
        }
        this.resetPreview();
        this.selectedFile.set(entry);
        if (entry.type === 'directory') return;
        const request = this.previewRequest;
        this.previewLoading.set(true);
        try {
            const info = await this.api.fileExplorerGetFileInfo(entry.path);
            if (request !== this.previewRequest) return;
            if (!info.success) throw new Error(info.error || this.t()('files.previewError'));
            this.fileInfo.set(info);
            if (!info.canPreview) return;
            const content = await this.api.fileExplorerReadFile(entry.path, info.previewType === 'text' ? 1024 * 1024 : undefined);
            if (request !== this.previewRequest) return;
            if (!content.success) throw new Error(content.error || this.t()('files.previewError'));
            this.previewContent.set(content);
            if (info.previewType === 'pdf' || info.previewType === 'image') {
                if (content.contentBase64 == null) throw new Error(this.t()('files.previewError'));
                const mime = info.previewType === 'pdf' ? 'application/pdf' : info.mimeType;
                if (!mime || (info.previewType === 'image' && !mime.startsWith('image/'))) throw new Error(this.t()('files.previewError'));
                this.previewObjectUrl = URL.createObjectURL(this.decodeBlob(content.contentBase64, mime));
                if (info.previewType === 'pdf') {
                    // Trust only this locally-created PDF blob, never a path or URL from the device.
                    this.pdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.previewObjectUrl));
                } else {
                    this.imageUrl.set(this.previewObjectUrl);
                }
            }
        } catch (error) {
            if (request === this.previewRequest) this.previewError.set(this.errorMessage(error));
        } finally {
            if (request === this.previewRequest) this.previewLoading.set(false);
        }
    }

    async closePreview(): Promise<void> {
        if (await this.canDeactivate()) this.resetPreview();
    }

    private resetPreview(): void {
        this.previewRequest++;
        this.revokePreviewUrl();
        this.selectedFile.set(null);
        this.previewContent.set(null);
        this.fileInfo.set(null);
        this.previewError.set(null);
        this.previewLoading.set(false);
        this.previewExpanded.set(false);
        this.editMode.set(false);
        this.editContent.set('');
    }

    private revokePreviewUrl(): void {
        if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
        this.previewObjectUrl = null;
        this.imageUrl.set(null);
        this.pdfUrl.set(null);
    }

    async canDeactivate(): Promise<boolean> {
        if (this.busy()) return false;
        if (!this.dirty()) return true;
        return this.confirm('files.discardTitle', this.t()('files.discardDetail'), 'files.discard');
    }

    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.dirty() || this.busy()) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    private confirm(title: string, message: string, accept: string): Promise<boolean> {
        return new Promise((resolve) =>
            this.confirmationService.confirm({
                header: this.t()(title),
                message,
                icon: 'fa fa-triangle-exclamation',
                acceptLabel: this.t()(accept),
                rejectLabel: this.t()('files.cancel'),
                acceptButtonProps: { severity: 'danger', size: 'small' },
                rejectButtonProps: { severity: 'secondary', outlined: true, size: 'small' },
                accept: () => resolve(true),
                reject: () => resolve(false)
            })
        );
    }

    enterEditMode(): void {
        if (!this.canEdit() || this.busy()) return;
        this.editContent.set(this.previewContent()?.content ?? '');
        this.editMode.set(true);
    }

    async cancelEdit(): Promise<void> {
        if (!(await this.canDeactivate())) return;
        this.editMode.set(false);
        this.editContent.set('');
    }

    async saveFile(): Promise<void> {
        const entry = this.selectedFile();
        if (!entry || !this.canEdit() || !this.dirty() || this.busy()) return;
        const content = this.editContent();
        this.editSaving.set(true);
        try {
            const result = await this.api.fileExplorerSaveFile(entry.path, content);
            if (!result.success) throw new Error(result.error || this.t()('files.operationFailed'));
            this.previewContent.update((preview) => (preview ? { ...preview, content } : preview));
            const updated = { ...entry, sizeBytes: result.sizeBytes ?? new TextEncoder().encode(content).length, modifiedUtc: result.modifiedUtc ?? entry.modifiedUtc };
            this.entries.update((entries) => entries.map((item) => (item.path === entry.path ? updated : item)));
            this.selectedFile.set(updated);
            this.fileInfo.update((info) => (info ? { ...info, sizeBytes: updated.sizeBytes, modifiedUtc: updated.modifiedUtc } : info));
            this.editMode.set(false);
            this.editContent.set('');
            this.notify('success', 'files.saved', entry.name);
        } catch (error) {
            this.notify('error', 'files.operationFailed', this.errorMessage(error));
        } finally {
            this.editSaving.set(false);
        }
    }

    async downloadFile(entry: FileExplorerEntry): Promise<void> {
        if (this.downloading()) return;
        this.downloading.set(true);
        try {
            const result = await this.api.fileExplorerDownloadFile(entry.path);
            if (!result.success || result.contentBase64 == null) throw new Error(result.error || this.t()('files.operationFailed'));
            const url = URL.createObjectURL(this.decodeBlob(result.contentBase64, result.mimeType || 'application/octet-stream'));
            const link = document.createElement('a');
            link.href = url;
            link.download = entry.name;
            document.body.appendChild(link);
            try {
                link.click();
            } finally {
                link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
            this.notify('success', 'files.downloaded', entry.name);
        } catch (error) {
            this.notify('error', 'files.operationFailed', this.errorMessage(error));
        } finally {
            this.downloading.set(false);
        }
    }

    async deleteFile(entry: FileExplorerEntry): Promise<void> {
        if (!(await this.canDeactivate()) || !(await this.confirm('files.delete', `${this.t()('files.deleteDetail')} "${entry.name}"?`, 'files.delete'))) return;
        this.deleting.set(true);
        try {
            const result = await this.api.fileExplorerDeleteFile(entry.path);
            if (!result.success) throw new Error(result.error || this.t()('files.operationFailed'));
            this.entries.update((entries) => entries.filter((item) => item.path !== entry.path));
            if (this.selectedFile()?.path === entry.path) this.resetPreview();
            this.notify('success', 'files.deleted', entry.name);
        } catch (error) {
            this.notify('error', 'files.operationFailed', this.errorMessage(error));
        } finally {
            this.deleting.set(false);
        }
    }

    async onFileInput(event: Event): Promise<void> {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        const files = Array.from(input.files ?? []);
        input.value = '';
        await this.uploadFiles(files);
    }

    async uploadFiles(files: File[]): Promise<void> {
        if (!files.length || this.loading() || this.error() || !(await this.canDeactivate())) return;
        const replacements = files.filter((file) => this.entries().some((entry) => entry.name === file.name));
        if (replacements.length && !(await this.confirm('files.replace', `${this.t()('files.replaceDetail')} ${replacements.map((file) => file.name).join(', ')}`, 'files.replace'))) return;
        const path = this.currentPath() || '/';
        this.uploadError.set(null);
        this.uploading.set(true);
        let uploadError: string | null = null;
        try {
            for (const [index, file] of files.entries()) {
                this.uploadStatus.set(`${index + 1} / ${files.length} · ${file.name}`);
                const result = await this.api.fileExplorerUploadFile(path, file);
                if (!result.success) throw new Error(`${file.name}: ${result.error || this.t()('files.operationFailed')}`);
                this.notify('success', 'files.uploaded', file.name);
            }
        } catch (error) {
            uploadError = this.errorMessage(error);
            this.notify('error', 'files.operationFailed', uploadError);
        } finally {
            await this.loadDirectory(this.currentPath());
            this.uploadError.set(uploadError);
            this.uploading.set(false);
            this.uploadStatus.set('');
        }
    }

    onFilesDragOver(event: DragEvent): void {
        event.preventDefault();
        if (this.busy() || this.loading() || this.error()) return;
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        this.isDragOver.set(true);
    }

    onFilesDragLeave(event: DragEvent): void {
        if (!(event.relatedTarget instanceof Node) || !(event.currentTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) this.isDragOver.set(false);
    }

    onFilesDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver.set(false);
        void this.uploadFiles(Array.from(event.dataTransfer?.files ?? []));
    }

    formatFileSize(bytes: number | undefined): string {
        if (bytes == null) return '—';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
        if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
        return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
    }

    formatDate(value: string | undefined): string {
        if (!value || !Number.isFinite(Date.parse(value))) return '—';
        return new Date(value).toLocaleString(this.translation.currentLanguage());
    }

    getFileIcon(entry: FileExplorerEntry): string {
        if (entry.isSymlink) return 'fa fa-link';
        if (entry.type === 'directory') return 'fa fa-folder';
        const extension = entry.extension?.toLowerCase() ?? '';
        if (/\.(png|jpe?g|gif|svg|webp|ico|bmp)$/.test(extension)) return 'fa fa-file-image';
        if (extension === '.pdf') return 'fa fa-file-pdf';
        if (/\.(zip|tar|gz|7z|rar|cpz)$/.test(extension)) return 'fa fa-file-zipper';
        if (/\.(json|xml|html?|css|scss|cs|ts|js|py|yml|yaml|sh)$/.test(extension)) return 'fa fa-file-code';
        if (/\.(txt|log|md|cfg|config|ini)$/.test(extension)) return 'fa fa-file-lines';
        return 'fa fa-file';
    }

    private decodeBlob(base64: string, mime: string): Blob {
        return new Blob([Uint8Array.from(atob(base64), (character) => character.charCodeAt(0))], { type: mime });
    }

    private notify(severity: 'success' | 'error', key: string, detail: string): void {
        this.messageService.add({ severity, summary: this.t()(key), detail, life: 5000 });
    }

    private errorMessage(error: unknown): string {
        if (error && typeof error === 'object' && 'error' in error) {
            const body = error.error;
            if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') return body.error;
        }
        if (error instanceof Error) return error.message;
        if (typeof error === 'string') return error;
        return this.t()('files.operationFailed');
    }
}
